import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { ImageDataDb } from '../../../_shared/data/image-data-db.data';
import { TrackingEvents } from '../../../_shared/data/tracking-events.data';
import { ICardFlipGrid } from '../../../_shared/models/card/card-flip-grid.model';
import { ICardFlip } from '../../../_shared/models/card/card-flip.model';
import { IDynamicElement } from '../../../_shared/models/dynamic/dynamic-element.model';
import { ImageData } from '../../../_shared/models/image/image-data.model';
import { GtmService } from '../../../_shared/services/gtm.service';
import { LinkService } from '../../../_shared/services/link.service';
import { withConfigFallback } from '../../../_shared/utils/with-config-fallback';
import { ContainerComponent } from '../../../container/container.component';
import { CardCollectionTextComponent } from '../card-collection-text/card-collection-text.component';
import { CardFlipComponent } from './card-flip/card-flip.component';

const MAX_ITEMS = 8;

@Component({
    selector: 'sirius-card-flip-grid',
    templateUrl: './card-flip-grid.component.html',
    styleUrls: ['./card-flip-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CardCollectionTextComponent, ContainerComponent, CardFlipComponent],
    host: {
        '[class.is-center]': "resolvedContentAlignment() === 'center'",
    },
})
export class CardFlipGridComponent implements IDynamicElement<ICardFlipGrid> {
    private readonly _gtmService = inject(GtmService);
    private readonly _linkService = inject(LinkService);

    readonly config = input<ICardFlipGrid | undefined>(undefined);

    readonly items = input<ICardFlip[]>([]);
    readonly title = input('');
    readonly textHtml = input('');
    readonly contentAlignment = input<'left' | 'center'>('left');

    readonly selectEvent = output<[ICardFlip, number]>();

    protected readonly resolvedItems = withConfigFallback<ICardFlipGrid, 'items', ICardFlip[]>(
        this.config,
        'items',
        this.items
    );
    protected readonly resolvedTitle = withConfigFallback<ICardFlipGrid, 'title', string>(
        this.config,
        'title',
        this.title
    );
    protected readonly resolvedTextHtml = withConfigFallback<ICardFlipGrid, 'textHtml', string>(
        this.config,
        'textHtml',
        this.textHtml
    );
    protected readonly resolvedContentAlignment = withConfigFallback<
        ICardFlipGrid,
        'contentAlignment',
        'left' | 'center'
    >(this.config, 'contentAlignment', this.contentAlignment);

    protected readonly visibleItems = computed(() => this.resolvedItems().slice(0, MAX_ITEMS));

    protected readonly imageData = computed<ImageData>(() => ImageDataDb.card.small);

    protected trackItem(item: ICardFlip, index: number): string {
        return item.image?.image?.url ?? item.link?.href ?? String(index);
    }

    onCardSelect(item: ICardFlip, index: number): void {
        this.selectEvent.emit([item, index]);

        const link = item.link;
        if (!link?.href?.trim()) {
            return;
        }

        const cfg = {
            ...link.tracking?.parameters,
            index,
            link_text: link.title,
            link_url: this._linkService.getFullPath(link.href),
        };

        this._gtmService.track(TrackingEvents.gtm['carouselSelect']([cfg]));
    }
}
